import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import { Button, Modal } from "antd";
import { ColDef } from "ag-grid-community";
import DefaultSelect from "../../components/form/DefaultSelect";
import useLang from "../../hooks/useLang";
import api from "../../utils/axios";
import {
	codeFormatter,
	priceFormatter,
	thousandsFormatter,
	valueDateFormatter,
} from "../../utils/formatters";
import { CheckCircleOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import "./ClientFinance.css";
import { ReactComponent as ExcelIcon } from "../../assets/excel-icon.svg";
import { ReactComponent as PrintIcon } from "../../assets/print-icon.svg";
import { Link } from "react-router-dom";
import PaginationTable from "../../components/Pagination";
import FinanceStatistic from "../../components/FinanceStatistic";
import { exportDataToCsv } from "../../utils/exportDataToCsv";
import { fetchStatus, fetchZones } from "../../lib/OptionListApi";
import { useDispatch } from "react-redux";
import { fetchFinances } from "../../lib/OrderAndFinanceApi";
import { financeStatistic } from "../../redux/financeSlice";
import { useMediaQuery } from "@mui/material";
import { CONFIG } from "../../config";

const Finance = () => {
	let defaultExcelExportSettings = {};
	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
			autoSizeAllColumns: true,
		}),
		[]
	);
	const dispatch = useDispatch();
	const [orderSales, setOrderSales] = useState<number>();
	const [orderRefusal, setOrderRefusal] = useState<number>();
	const [tableColumns, setTableColumns] = useState<ColDef[]>();
	const [tableColumns2, setTableColumns2] = useState<ColDef[]>();
	const [tableData, setTableData] = useState([]);
	const [settlementData, setSettlementData] = useState<any[]>([]);
	const [cashFlow, setCashFlow] = useState<number>();
	const [open, setOpen] = useState<boolean>(false);
	const [settlementId, setSettlementId] = useState<number>();
	const [settlementValue, setSettlementValue] = useState<number>();
	const [startDataIndex, setStartDataIndex] = useState<any>();
	const [endDataIndex, setEndDataIndex] = useState<any>();
	const [endDataIndexForSettlements, setEndDataIndexForSettlements] =
		useState<any>();
	const [startDataIndexSettlements, setStartDataIndexSettlements] =
		useState<any>();
	const [pageSizeFinance, setPageSizeFinance] = useState(20);
	const [pageSizeSettlements, setPageSizeSettlements] = useState(20);
	const [currentPageFinance, setCurrentPageFinance] = useState(1);
	const [currentPageSettlements, setCurrentPageSettlements] = useState(1);
	let grid = useRef(null);
	let settlementGridRef = useRef(null);
	const [companyBalance, setCompanyBalance] = useState<any>([]);
	const [country, setCountry] = useState<string | undefined>();
	const [city, setCity] = useState<string | undefined>();
	const [countryDropDown, setCountryDropDown] = useState<any>([]);
	const [cityDropDown, setCityDropDown] = useState<any>([]);
	const [cities, setCities] = useState<any>([]);
	const [zones, setZones] = useState<any>([]);
	const [settlementStatusOptions, setSettlementStatusOptions] =
		useState<any>();
	const [selectedStatus, setSelectedStatus] = useState(null);

	const hexToRgba = (hex: string, alpha: number) => {
		const r = parseInt(hex.slice(1, 3), 16);
		const g = parseInt(hex.slice(3, 5), 16);
		const b = parseInt(hex.slice(5, 7), 16);
		return `rgba(${r},${g},${b},${alpha})`;
	};

	const { strings } = useLang("finance");

	const getFinances = async () => {
		const result = await fetchFinances();
		dispatch(financeStatistic(result));
	};

	const getTableData = async () => {
		try {
			const response = await api.get("Order/fetch-orders-for-settlement");
			if (response) {
				setTableData(response.data);
			}
		} catch (error) {
			console.log(error);
			setTableData([]);
		}
	};
	const fetchSettlements = async () => {
		try {
			const response = await api.get("Settlement/selectsettlements");
			if (response) {
				setSettlementData(response?.data);
			}
		} catch (error) {
			console.log(error);
			setSettlementData([]);
		}
	};

	const fetchCashFlow = async () => {
		try {
			const response = await api.get("Balance/client-cash-flow");
			if (response) {
				setCashFlow(response?.data);
			}
		} catch (error) {
			console.log(error);
			setCashFlow(0.0);
		}
	};

	const ClientBalance = async () => {
		try {
			const response = await api.get("Balance/client-balance");
			if (response) {
				const result = response.data;
				setOrderSales(result[0].salesBalance);
				setOrderRefusal(result[0].refusalPrice);
			}
		} catch (error) {
			console.log(
				"Error fetching financial balance data from the API:",
				error
			);
			setOrderRefusal(0);
			setOrderSales(0);
		}
	};

	const getClientCompanyFinance = async () => {
		try {
			const response = await api.get("Finance/client-finance");
			if (response) {
				const result = response.data[0];
				setCompanyBalance(result);
			}
		} catch (error) {
			console.log(error);
			setCompanyBalance([]);
		}
	};

	const getStatuses = useCallback(async () => {
		const statuses = await fetchStatus();

		if (statuses) {
			const orderStatuses = statuses.map((item: any) => ({
				value: item.id,
				label: item.description,
			}));
			setSettlementStatusOptions(orderStatuses);
		}
	}, []);

	const getZones = useCallback(async () => {
		const allZones = await fetchZones();
		if (allZones) {
			const countries = allZones
				.filter((item: any) => item.levelId === 1)
				.map((item: any) => ({
					value: item.id,
					label: item.description,
				}));
			setCountryDropDown(countries);
			const filteredCities = allZones
				.filter((item: any) => item.levelId === 2)
				.map((item: any) => ({
					value: item.id,
					label: item.description,
				}));
			setCityDropDown(filteredCities);
			const cities = allZones?.filter((item: any) => item.levelId === 2);
			setCities(cities);
		}
		setZones(allZones);
	}, [zones]);

	const onCountrySelect = (e: any) => {
		setCity("");
		const filteredCities = cities
			.filter((item: any) => item.parentId === e)
			.map((item: any) => ({
				value: item.id,
				label: item.description,
			}));
		setCityDropDown(filteredCities);
		setCountry(e);
		setCurrentPageFinance(1);
	};

	const isSmallScreen = useMediaQuery("(max-width: 994px)");

	useEffect(() => {
		getFinances();
		setTableColumns([
			{
				field: "displayId",
				headerName: strings?.code,
				valueFormatter: (params) => codeFormatter(params.value),
				// type: "numericColumn",
				// filter: "agNumberColumnFilter",
				cellStyle: (params: any) => ({
					fontSize: "15px",
					fontWeight: "400",
					lineHeight: "18px",
					color: "#4F16C2",
					textDecoration: "underline",
				}),
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "statusDescription",
				headerName: strings?.status,
				valueFormatter: (params) => {
					if (params.value === 6) {
						return "Dorezuar";
					}
					return params.value;
				},
				cellRendererFramework: (params: any) => {
					const statusColorHexCode =
						params.data.statusColorHexCode || "#ffff";
					const rgbaColor = hexToRgba(statusColorHexCode, 0.08);
					return (
						<div className="statusStyle">
							<div
								className="statusValues"
								style={{
									backgroundColor: rgbaColor,
									color: statusColorHexCode,
								}}
							>
								<p className="text-lg">&#x2022;</p>
								<p className="orderStatusDescription">
									{params.value}
								</p>
							</div>
						</div>
					);
				},
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "country",
				headerName: strings?.countrylabel,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "city",
				headerName: strings?.citylabel,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "buyerNameAndSurname",
				headerName: strings?.buyer,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "orderPrice",
				headerName: strings?.orderPrice,
				valueFormatter: (params) => priceFormatter(params.value),
				maxWidth: 130,
				suppressSizeToFit: isSmallScreen,
			},
		]);
		setTableColumns2([
			{
				field: "id",
				headerName: strings?.code,
				valueFormatter: (params) => codeFormatter(params.value),
				cellStyle: (params: any) => ({
					fontSize: "15px",
					fontWeight: "400",
					lineHeight: "18px",
					color: "#4F16C2",
					textDecoration: "underline",
				}),
				width: 120,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "settlementStatusDescription",
				headerName: strings?.status,
				valueFormatter: (params) => {
					if (params.value === 6) {
						return "Dorezuar";
					}
					return params.value;
				},
				cellRendererFramework: (params: any) => {
					const statusColorHexCode =
						params.data.statusColorHexCode || "#ffff";
					const rgbaColor = hexToRgba(statusColorHexCode, 0.08);
					return (
						<div className="statusStyle">
							<div
								className="statusValues"
								style={{
									backgroundColor: rgbaColor,
									color: statusColorHexCode,
								}}
							>
								<p className="text-lg">&#x2022;</p>
								<p className="orderStatusDescription">
									{params.value}
								</p>
							</div>
						</div>
					);
				},
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "createdAt",
				headerName: strings?.date,
				valueFormatter: (params) => valueDateFormatter(params?.value),
				width: 180,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "numberOfOrders",
				headerName: strings?.orderCount,
				width: 120,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "sumValue",
				headerName: strings?.value,
				width: 100,
				valueFormatter: (params) => priceFormatter(params.value),
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "settlementDescription",
				headerName: strings?.comments,
				width: 180,
				cellStyle: {
					whiteSpace: "normal",
					overflowWrap: "break-word",
					wordWrap: "break-word",
				},
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "applicantFullName",
				headerName: strings?.responsibility,
				width: 140,
				suppressSizeToFit: isSmallScreen,
			},
			{
				headerName: strings?.confirmed,
				field: "settlementStatusId",
				valueFormatter: (params) => valueDateFormatter(params?.value),
				width: 200,
				hide: true,
				cellRenderer: (params: any) => {
					if (params?.value === 4) {
						return (
							<Button
								icon={<CheckCircleOutlined />}
								style={{ color: "green" }}
							>
								{strings?.confirm}
							</Button>
						);
					} else if (params?.value === 5) {
						return valueDateFormatter(params?.data.confirmedDate);
					} else {
						return params?.value;
					}
				},
				suppressSizeToFit: isSmallScreen,
			},
		]);
		fetchCashFlow();
		ClientBalance();
		fetchSettlements();
		getStatuses();
		getZones();
		getTableData();
		getClientCompanyFinance();
		if (tableData) {
			const startDataIndex =
				(currentPageFinance - 1) * pageSizeFinance + 1;
			setStartDataIndex(startDataIndex);
			const endDataIndex =
				startDataIndex +
				Math.min(pageSizeFinance, tableData.length - startDataIndex);
			setEndDataIndex(endDataIndex);
			const startDataIndexSettlements =
				(currentPageSettlements - 1) * pageSizeSettlements + 1;
			setStartDataIndexSettlements(startDataIndexSettlements);
			const endDataIndexSettlements =
				startDataIndexSettlements +
				Math.min(
					pageSizeSettlements,
					settlementData.length - startDataIndexSettlements
				);
			setEndDataIndexForSettlements(endDataIndexSettlements);
		}
	}, [setTableColumns, setTableColumns2, strings]);

	const onCellClicked = async (params: any) => {
		const colId = params.column.colId;
		switch (colId) {
			case "displayId":
				await onOrderIdClicked(params?.data);
				break;
		}
	};

	const onOrderIdClicked = (data: any) => {
		const orderId = data?.id?.toString();
		// navigate("/order-details", { state: orderId });
		const url = `/order-details?id=${orderId}`;
		window.open(url, "_blank");
	};

	const onSettlementGridClicked = async (params: any) => {
		const colId = params.column.colId;
		switch (colId) {
			case "settlementStatusId":
				await onSettlementStatusClicked(params?.data);
				break;
		}
	};
	const onSettlementStatusClicked = async (data: any) => {
		if (data.settlementStatusId === 4) {
			const settlementId = data?.id;
			const settlementValue = data?.sumValue;
			setSettlementId(settlementId);
			setSettlementValue(settlementValue);
			setOpen(true);
		}
	};

	const [
		isConfirmSettlementOrdersDisabled,
		setIsConfirmSettlementOrdersDisabled,
	] = useState(false);

	const SettlemenntConfirmStatus = async () => {
		setIsConfirmSettlementOrdersDisabled(true);

		try {
			const response = await api.post(
				"Settlement/client-settlement-confirmation",
				{ settlementId: settlementId }
			);
			if (response.status === 200) {
				toast.success("Barazimi u konfirmua me sukses!");
				setOpen(false);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setIsConfirmSettlementOrdersDisabled(false);
		}
	};

	const onPageFinanceChanged = (page: any) => {
		setCurrentPageFinance(page);
	};
	const handleFinancePageSizeChange = (current: number, size: number) => {
		setPageSizeFinance(size);
		setCurrentPageFinance(1);
	};
	const onPageSettlementsChanged = (page: any) => {
		setCurrentPageSettlements(page);
	};
	const handleSettlementPageSizeChange = (current: number, size: number) => {
		setPageSizeSettlements(size);
		setCurrentPageSettlements(1);
	};

	const paginatedFinanceData = useMemo(() => {
		const startIndex = (currentPageFinance - 1) * pageSizeFinance;
		const endIndex = startIndex + pageSizeFinance;
		return tableData.slice(startIndex, endIndex);
	}, [currentPageFinance, pageSizeFinance, tableData]);

	const paginatedSettlementsData = useMemo(() => {
		const startIndex = (currentPageSettlements - 1) * pageSizeSettlements;
		const endIndex = startIndex + pageSizeSettlements;
		return settlementData?.slice(startIndex, endIndex);
	}, [currentPageSettlements, pageSizeSettlements, settlementData]);

	const filteredData = useMemo(() => {
		return paginatedFinanceData.filter((item: any) => {
			return (
				(!city || item.cityId === city) &&
				(!country || item.countryId === country) &&
				(!selectedStatus || item.statusId === selectedStatus)
			);
		});
	}, [city, country, paginatedFinanceData, selectedStatus]);

	const selectedColumnIds = [
		"id",
		"statusId",
		"country",
		"city",
		"buyerNameAndSurname",
		"orderPrice",
	];

	const onExportSelectedId = () => {
		const dataToExport = filteredData.map((item: any) => ({
			...item,
			displayId: codeFormatter(item.displayId),
			statusDescription:
				item.statusDescription === 6
					? "Dorezuar"
					: item.statusDescription,
			orderPrice: thousandsFormatter(item.orderPrice),
		}));
		exportDataToCsv(grid, selectedColumnIds, dataToExport);
	};

	const onFirstDataRendered = useCallback((params: any) => {
		(grid.current as any).api.sizeColumnsToFit();
		(settlementGridRef.current as any).api.sizeColumnsToFit();
	}, []);

	return (
		<>
			<div className="mainFinance">
				<FinanceStatistic />
				<div className="secondFinance">
					<div className="secondTable">
						<div className="secondFinanceDiv">
							<h1 className="bilanceTitle">
								{strings?.secondTitle}
							</h1>
							<div className="buttonsFinance">
								<Button
									className="buttonTable"
									onClick={onExportSelectedId}
								>
									<ExcelIcon />
									Excel
								</Button>
								<Button className="buttonTable">
									<PrintIcon />
									{strings?.print}
								</Button>
							</div>
						</div>
						<div className="financeInputs">
							<DefaultSelect
								label={strings?.statuslabel}
								flexDirection="col"
								alignCenter="start"
								className="financeinput"
								placeholder={strings?.choose}
								options={settlementStatusOptions}
								value={selectedStatus}
								onChange={(value: any) => {
									setCurrentPageFinance(1);
									setSelectedStatus(value);
								}}
								allowClear
							/>
							<DefaultSelect
								label={strings?.countrylabel}
								flexDirection="col"
								alignCenter="start"
								className="financeinput"
								placeholder={strings?.choose}
								options={countryDropDown}
								value={country}
								onChange={onCountrySelect}
								allowClear
							/>
							<DefaultSelect
								label={strings?.citylabel}
								flexDirection="col"
								alignCenter="start"
								className="financeinput"
								placeholder={strings?.choose}
								options={cityDropDown}
								value={city}
								onChange={(e: any) => {
									setCurrentPageFinance(1);
									setCity(e);
								}}
								allowClear
							/>
						</div>
						<PaginationTable
							current={currentPageFinance}
							pageSize={pageSizeFinance}
							total={tableData.length}
							startOrderIndex={startDataIndex}
							endOrderIndex={endDataIndex}
							onChange={onPageFinanceChanged}
							onShowSizeChange={handleFinancePageSizeChange}
						/>
						<div
							className="ag-theme-alpine"
							style={{
								width: "100%",
								overflow: "auto",
								padding: 8,
							}}
						>
							<AgGridReact
								rowData={filteredData}
								columnDefs={tableColumns}
								defaultColDef={defaultColDef}
								undoRedoCellEditing={true}
								undoRedoCellEditingLimit={20}
								suppressCopyRowsToClipboard={true}
								defaultExcelExportParams={
									defaultExcelExportSettings
								}
								rowSelection="multiple"
								domLayout="autoHeight"
								ref={grid}
								enableRangeSelection={true}
								cacheQuickFilter={true}
								onCellClicked={onCellClicked}
								rowHeight={60}
								onFirstDataRendered={onFirstDataRendered}
							/>
						</div>
					</div>
				</div>
				<div className="thirdFinance">
					<div className="thirdTable">
						<div className="financeSettlementDiv">
							<h1 className="financeSettlementTitle">
								{strings?.lastdraws}
							</h1>
							<Link
								to="/settlement"
								className="font-sans font-medium text-sm leading-[17px] text-[#4F16C2]"
							>
								{strings?.showAll} →{" "}
							</Link>
						</div>
						<PaginationTable
							current={currentPageSettlements}
							pageSize={pageSizeSettlements}
							total={settlementData?.length}
							startOrderIndex={startDataIndexSettlements}
							endOrderIndex={endDataIndexForSettlements}
							onChange={onPageSettlementsChanged}
							onShowSizeChange={handleSettlementPageSizeChange}
						/>
						<div
							className="ag-theme-alpine"
							style={{
								width: "100%",
								overflow: "auto",
								padding: 8,
							}}
						>
							<AgGridReact
								rowData={paginatedSettlementsData}
								columnDefs={tableColumns2}
								defaultColDef={defaultColDef}
								undoRedoCellEditing={true}
								undoRedoCellEditingLimit={20}
								suppressCopyRowsToClipboard={true}
								onCellClicked={onSettlementGridClicked}
								defaultExcelExportParams={
									defaultExcelExportSettings
								}
								ref={settlementGridRef}
								domLayout="autoHeight"
								enableRangeSelection={true}
								cacheQuickFilter={true}
								rowHeight={60}
								onFirstDataRendered={onFirstDataRendered}
							/>
						</div>
					</div>
				</div>
			</div>
			<Modal
				open={open}
				onCancel={() => setOpen(false)}
				title={strings?.confirmSettlement}
				footer={(_, { CancelBtn }) => (
					<>
						<Button
							icon={<CheckCircleOutlined />}
							style={{ color: "white", backgroundColor: "green" }}
							type="primary"
							onClick={SettlemenntConfirmStatus}
							disabled={isConfirmSettlementOrdersDisabled}
						>
							{strings?.confirm}
						</Button>
						<CancelBtn />
					</>
				)}
			>
				<p>
					{`${strings?.confirmDesc} ${settlementId} ${strings?.confirmDescAmount} ${settlementValue?.toFixed(
						2
					)}${CONFIG?.CURRENCY}`}
				</p>
			</Modal>
		</>
	);
};

export default Finance;
